import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Download, Phone, MessageCircle } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { BRAND } from "../lib/brandConfig";

/**
 * Post-wizard confirmation page. Lives at /get-started/thank-you.
 * Receives `firstName` via router state when reached from the wizard;
 * gracefully degrades to a generic greeting if linked directly.
 */
const GetStartedThankYou = () => {
  const { state } = useLocation();
  const firstName = state?.firstName?.trim() || "";

  useSEO({
    title: "You're all set · Urban Nest Quest",
    description: "Bryan will be in touch within the hour with your top 3 Houston apartment matches.",
    path: "/get-started/thank-you",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#FAF7F2] flex items-center px-6 py-20" data-testid="thank-you-page">
      <div className="w-full max-w-xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <p className="mt-6 text-xs uppercase tracking-widest text-[#DE6E4B] font-bold">
          You're all set
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display'] leading-tight">
          {firstName ? `Thanks, ${firstName} — ` : ""}Bryan will be in touch within the hour
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Your top 3 Houston apartment matches are being pulled now. Bryan personally reviews every match before texting you — so you only see places that actually fit.
        </p>

        <div className="mt-8 bg-white rounded-2xl border-2 border-slate-200 p-6 text-left" data-testid="thank-you-checklist-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#DE6E4B]/10 text-[#DE6E4B] flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-[#1F2A44]">In the meantime…</p>
              <p className="text-sm text-slate-600 mt-1">
                Grab your free Houston Renter's Checklist. Cover everything from pet deposits to credit score requirements.
              </p>
              <a
                href="/downloads/Houston_Renters_Checklist_UrbanNestQuest.pdf"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#1F2A44] text-white rounded-lg text-sm font-bold hover:bg-[#2a3a5e]"
                data-testid="thank-you-checklist-download"
                download="Houston_Renters_Checklist_UrbanNestQuest.pdf"
              >
                <Download className="w-4 h-4" /> Download Checklist (PDF)
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <a
            href={BRAND.phoneHref || `tel:${BRAND.phone || ""}`}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700"
            data-testid="thank-you-call-bryan"
          >
            <Phone className="w-4 h-4" /> Call Bryan
          </a>
          <a
            href={BRAND.smsHref || "#"}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-[#1F2A44] text-[#1F2A44] rounded-xl font-bold hover:bg-[#1F2A44] hover:text-white"
            data-testid="thank-you-text-bryan"
          >
            <MessageCircle className="w-4 h-4" /> Text instead
          </a>
        </div>

        <p className="mt-6 text-xs text-slate-400">
          Need to change something? <Link to="/get-started" className="underline">Update your preferences</Link>
        </p>
      </div>
    </div>
  );
};

export default GetStartedThankYou;
